import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  FiLock,
  FiFileText,
  FiDatabase,
  FiCheckCircle,
  FiShield,
  FiShare2,
  FiUserCheck,
  FiUsers,
  FiRefreshCw,
  FiMail,
} from "react-icons/fi";
import Header from "../../components/layouts/Header";
import Footer from "../../components/layouts/Footer";
import ContactStrip from "./ContactStrip";
import "./FooterPages.css";

const SECTIONS = [
  { id: "intro", label: "Introduction", icon: <FiLock /> },
  { id: "collect", label: "Information We Collect", icon: <FiFileText /> },
  { id: "use", label: "How We Use Information", icon: <FiDatabase /> },
  { id: "cookies", label: "Cookies", icon: <FiCheckCircle /> },
  { id: "security", label: "Data Security", icon: <FiShield /> },
  { id: "sharing", label: "Sharing of Information", icon: <FiShare2 /> },
  { id: "rights", label: "Your Rights", icon: <FiUserCheck /> },
  { id: "children", label: "Children's Privacy", icon: <FiUsers /> },
  { id: "updates", label: "Policy Updates", icon: <FiRefreshCw /> },
  { id: "contact", label: "Contact Us", icon: <FiMail /> },
];

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState("intro");

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="mgh-info-page-wrapper">
      <Helmet>
        <title>Privacy Policy | My Golden Homes</title>
        <meta
          name="description"
          content="Your privacy matters to us. Learn how we collect, use, protect, and manage your information on My Golden Homes."
        />
      </Helmet>

      {/* Unchanged Header */}
      <Header />

      {/* Hero Banner */}
      <section className="mgh-info-hero">
        <div className="mgh-info-hero-inner">
          <div className="mgh-info-hero-left">
            <div className="mgh-info-breadcrumb">
              <Link to="/">Home</Link>
              <span>&gt;</span>
              <span>Privacy Policy</span>
            </div>
            <h1 className="mgh-info-hero-title">
              Privacy <span>Policy</span>
            </h1>
            <p className="mgh-info-hero-subtitle">
              Your privacy matters to us. Learn how we collect, use, protect and manage your information on My Golden Homes.
            </p>

            <div className="mgh-hero-pills-row">
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiShield />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>100% Secure</strong>
                  <span>Bank-Grade Encryption</span>
                </div>
              </div>
              <div className="mgh-hero-pill">
                <div className="mgh-hero-pill-icon">
                  <FiCheckCircle />
                </div>
                <div className="mgh-hero-pill-text">
                  <strong>GDPR &amp; IT Act</strong>
                  <span>Compliant Standards</span>
                </div>
              </div>
            </div>
          </div>

          <div className="mgh-info-hero-art">
            <img
              src="/assets/images/villa_header_banner.jpg"
              alt="My Golden Homes Privacy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/assets/images/why_choose_villa.jpg";
              }}
            />
          </div>
        </div>
      </section>

      {/* Main Grid: Sidebar + Policy Sections */}
      <div className="mgh-info-container">
        <div className="mgh-info-grid-sidebar">
          {/* Left Navigation Sidebar */}
          <aside className="mgh-sidebar-nav-card">
            {SECTIONS.map((sec) => (
              <button
                type="button"
                key={sec.id}
                className={`mgh-sidebar-nav-item ${activeSection === sec.id ? "active" : ""}`}
                onClick={() => scrollToSection(sec.id)}
              >
                <span className="mgh-sidebar-nav-icon">{sec.icon}</span>
                <span>{sec.label}</span>
              </button>
            ))}
          </aside>

          {/* Right Policy Document Content */}
          <div className="mgh-info-main-content">
            {/* 1. Introduction */}
            <section id="intro" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiLock /> Introduction
              </h2>
              <p className="mgh-info-text">
                This Privacy Policy is subject to the terms of the Site Policy (User Agreement) of Mygoldenhomes.com. This policy is effective from the date and time a user registers with Mygoldenhomes by filling up the Registration form and accepting the terms and conditions laid out in the Site Policy.
              </p>
              <p className="mgh-info-text">
                We are committed to maintaining the confidentiality, integrity, and security of any personal information of our users. We treat your personal data with utmost care and adhere to all Indian Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.
              </p>
            </section>

            {/* 2. Information We Collect */}
            <section id="collect" className="mgh-info-section">
              <div className="mgh-info-callout">
                <div className="mgh-callout-icon">
                  <FiDatabase />
                </div>
                <div className="mgh-callout-text">
                  <h4>Information We Collect</h4>
                  <p>
                    In order to provide a personalized browsing experience, Mygoldenhomes may collect personal information from you. Additionally, some of our websites may require you to complete a registration form or seek details from you.
                  </p>
                </div>
              </div>
              <p className="mgh-info-text">
                When you register, submit queries, post properties, or request assistance from builders or owners, we collect your name, email address, contact telephone number, property preferences, and budget requirements.
              </p>
            </section>

            {/* 3. How We Use Information */}
            <section id="use" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiFileText /> How We Use Information
              </h2>
              <p className="mgh-info-text">
                We use your information to verify listings, facilitate seamless contact with verified property sellers, improve our search recommendation engines, and keep you notified of relevant real estate opportunities and price movements.
              </p>
            </section>

            {/* 4. Cookies */}
            <section id="cookies" className="mgh-info-section">
              <div className="mgh-info-callout" style={{ borderLeftColor: "#10b981" }}>
                <div className="mgh-callout-icon" style={{ background: "#ecfdf5", color: "#059669" }}>
                  <FiCheckCircle />
                </div>
                <div className="mgh-callout-text">
                  <h4>Cookies &amp; Local Storage</h4>
                  <p>
                    To extend this personalized experience, Mygoldenhomes may track the IP address of a user&apos;s computer and save certain information on your system in the form of cookies or local storage.
                  </p>
                </div>
              </div>
              <p className="mgh-info-text">
                A user has the option of accepting or declining the cookies of this website by changing the settings of your browser. Disabling cookies may limit certain interactive features such as saved shortlists.
              </p>
            </section>

            {/* 5. Data Security */}
            <section id="security" className="mgh-info-section">
              <div className="mgh-info-callout" style={{ borderLeftColor: "#f59e0b" }}>
                <div className="mgh-callout-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
                  <FiShield />
                </div>
                <div className="mgh-callout-text">
                  <h4>Data Security Standards</h4>
                  <p>
                    We take reasonable security measures to protect your personal information from unauthorized access, alteration, disclosure or destruction. All user logins and financial interactions are encrypted using 256-bit SSL protocols.
                  </p>
                </div>
              </div>
            </section>

            {/* 6. Sharing of Information */}
            <section id="sharing" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiShare2 /> Sharing of Information
              </h2>
              <p className="mgh-info-text">
                We never sell or rent your personal contact information to unaffiliated third-party marketing brokers. Contact details are shared solely with verified property owners or authorized builders when you explicitly click "Contact Owner" or "Request Callback".
              </p>
            </section>

            {/* 7. Your Rights */}
            <section id="rights" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiUserCheck /> Your Rights &amp; Controls
              </h2>
              <p className="mgh-info-text">
                You have the right to access, update, review, or permanently delete your account and personal records at any time from your My MGH Dashboard or by reaching out to our Grievance Officer.
              </p>
            </section>

            {/* 8. Children's Privacy */}
            <section id="children" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiUsers /> Children&apos;s Privacy
              </h2>
              <p className="mgh-info-text">
                Mygoldenhomes.com is intended for users who are at least 18 years of age and legally competent to enter into contracts under the Indian Contract Act, 1872.
              </p>
            </section>

            {/* 9. Policy Updates */}
            <section id="updates" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiRefreshCw /> Policy Updates
              </h2>
              <p className="mgh-info-text">
                We reserve the right to modify this Privacy Policy from time to time. Any material changes will be announced with an updated revision date at the top of this page.
              </p>
            </section>

            {/* 10. Contact Us */}
            <section id="contact" className="mgh-info-section">
              <h2 className="mgh-info-section-title">
                <FiMail /> Contact Us
              </h2>
              <p className="mgh-info-text">
                For questions regarding this privacy policy or to exercise any data rights, please email our Privacy and Grievance desk at <strong>feedback@mygoldenhomes.com</strong>.
              </p>
            </section>
          </div>
        </div>

        {/* Contact Strip */}
        <ContactStrip customEmail="feedback@mygoldenhomes.com" />
      </div>

      {/* Unchanged Footer */}
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;